import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { oauth, APIError, type AuthorizeContext, getApiUrl, isOrgMode } from '../api/client'
import { useOrg } from '../hooks/useOrg'
import { AuthLayout } from '../components/layout/AuthLayout'
import {
  AppInfo,
  ConsentButtons,
  ConsentCard,
  ScopeList,
  UserInfo,
} from '../components/consent'
import { PoweredByFooter } from '../components/OrgBranding'
import { Alert, Spinner } from '../components/ui'

type AuthorizeState =
  | { status: 'loading' }
  | { status: 'needs_login' }
  | { status: 'needs_consent'; context: AuthorizeContext }
  | { status: 'error'; error: string; canRedirect: boolean }

export function AuthorizePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<AuthorizeState>({ status: 'loading' })
  const [submitting, setSubmitting] = useState(false)
  const orgContext = useOrg()

  // Extract OAuth params
  const clientId = searchParams.get('client_id')
  const redirectUri = searchParams.get('redirect_uri')
  const scope = searchParams.get('scope')
  const stateParam = searchParams.get('state')
  const codeChallenge = searchParams.get('code_challenge')
  const codeChallengeMethod = searchParams.get('code_challenge_method') || 'S256'
  const nonce = searchParams.get('nonce')
  const prompt = searchParams.get('prompt')

  useEffect(() => {
    // Validate required params
    if (!clientId || !redirectUri || !scope) {
      setState({
        status: 'error',
        error: 'Missing required OAuth parameters',
        canRedirect: false,
      })
      return
    }

    // For prompt=none (silent auth), skip UI and go directly to API
    // The API handles prompt=none by returning code or error redirect
    if (prompt === 'none') {
      window.location.href = buildAuthorizeUrl()
      return
    }

    // Fetch authorization context
    const fetchContext = async () => {
      try {
        const context = await oauth.getAuthorizeContext({
          client_id: clientId,
          redirect_uri: redirectUri,
          scope: scope,
          state: stateParam || undefined,
          code_challenge: codeChallenge || undefined,
          code_challenge_method: codeChallengeMethod,
          nonce: nonce || undefined,
        })

        // If already consented, redirect to backend authorize endpoint
        // which will issue the code and redirect back
        if (context.already_consented) {
          window.location.href = buildAuthorizeUrl()
          return
        }

        // Show consent screen
        setState({ status: 'needs_consent', context })
      } catch (err) {
        if (err instanceof APIError) {
          if (err.status === 401) {
            // User not logged in - redirect to login
            setState({ status: 'needs_login' })
            return
          }
          setState({
            status: 'error',
            error: err.message,
            canRedirect: false,
          })
        } else {
          setState({
            status: 'error',
            error: 'An unexpected error occurred',
            canRedirect: false,
          })
        }
      }
    }

    fetchContext()
  }, [clientId, redirectUri, scope, stateParam, codeChallenge, codeChallengeMethod, nonce, prompt])

  const buildAuthorizeUrl = () => {
    const params = new URLSearchParams()
    params.set('response_type', 'code')
    params.set('client_id', clientId!)
    params.set('redirect_uri', redirectUri!)
    params.set('scope', scope!)
    if (stateParam) params.set('state', stateParam)
    if (codeChallenge) params.set('code_challenge', codeChallenge)
    if (codeChallengeMethod) params.set('code_challenge_method', codeChallengeMethod)
    if (nonce) params.set('nonce', nonce)
    if (prompt) params.set('prompt', prompt)

    // In production org mode, redirect to the backend API directly
    // In development, use same-origin path to go through Vite proxy
    const baseUrl = isOrgMode() && window.location.hostname.includes('.u.software')
      ? getApiUrl()
      : ''
    return `${baseUrl}/oauth/authorize?${params.toString()}`
  }

  const handleAllow = async () => {
    if (state.status !== 'needs_consent') return

    setSubmitting(true)

    try {
      // Submit consent via POST to the consent endpoint
      const response = await oauth.submitConsent({
        client_id: clientId!,
        redirect_uri: redirectUri!,
        scope: scope!,
        approved_scopes: scope!, // All requested scopes
        state: stateParam || undefined,
        code_challenge: codeChallenge || undefined,
        code_challenge_method: codeChallengeMethod,
        nonce: nonce || undefined,
      })

      if (!response.ok) {
        throw new Error('Consent submission failed')
      }

      // Backend returns JSON with redirect_url
      const data = await response.json()
      if (data.redirect_url) {
        window.location.href = data.redirect_url
      }
    } catch (err) {
      setSubmitting(false)
      setState({
        status: 'error',
        error: 'Failed to submit consent',
        canRedirect: true,
      })
    }
  }

  const handleDeny = () => {
    if (!redirectUri) return

    // Redirect with access_denied error
    const url = new URL(redirectUri)
    url.searchParams.set('error', 'access_denied')
    url.searchParams.set('error_description', 'User denied consent')
    if (stateParam) url.searchParams.set('state', stateParam)

    window.location.href = url.toString()
  }

  const handleSwitchAccount = () => {
    // Redirect to login with return URL
    const returnTo = window.location.href
    navigate(`/login?return_to=${encodeURIComponent(returnTo)}`)
  }

  // Redirect to login if not authenticated
  if (state.status === 'needs_login') {
    const returnTo = window.location.href
    window.location.href = `/login?return_to=${encodeURIComponent(returnTo)}`
    return null
  }

  // Loading state
  if (state.status === 'loading') {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-[var(--text-secondary)]">
            Loading authorization...
          </p>
        </div>
      </AuthLayout>
    )
  }

  // Error state
  if (state.status === 'error') {
    return (
      <AuthLayout>
        <ConsentCard>
          <Alert variant="error" className="mb-4">
            {state.error}
          </Alert>
          {state.canRedirect && redirectUri && (
            <button
              onClick={handleDeny}
              className="w-full rounded-lg bg-[var(--bg-elevated)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-elevated-2)]"
            >
              Return to application
            </button>
          )}
        </ConsentCard>
      </AuthLayout>
    )
  }

  // Consent screen
  const { context } = state
  const isIncremental = context.new_scopes.length < context.requested_scopes.length

  return (
    <AuthLayout>
      <ConsentCard>
        {/* App Info */}
        <div className="mb-6">
          <AppInfo
            name={context.app.name}
            logoUrl={context.app.logo_url}
            homepageUrl={context.app.homepage_url}
          />
        </div>

        {/* Separator */}
        <div className="mb-6 border-t border-[var(--border-subtle)]" />

        {/* Message */}
        <p className="mb-4 text-sm text-[var(--text-secondary)]">
          {isIncremental
            ? `${context.app.name} is requesting additional access`
            : `${context.app.name} wants to access your account`}
        </p>

        {/* Scopes */}
        <div className="mb-6">
          <ScopeList
            scopes={context.requested_scopes}
            newScopes={context.new_scopes}
            title={isIncremental ? 'New permissions:' : 'This will allow the app to:'}
          />
        </div>

        {/* Separator */}
        <div className="mb-6 border-t border-[var(--border-subtle)]" />

        {/* User Info */}
        <div className="mb-6">
          <UserInfo
            email={context.user.email}
            displayName={context.user.display_name}
            onSwitchAccount={handleSwitchAccount}
          />
        </div>

        {/* Action Buttons */}
        <ConsentButtons
          onAllow={handleAllow}
          onDeny={handleDeny}
          loading={submitting}
        />

        {/* Powered by footer for org-branded consent */}
        {orgContext.slug && <PoweredByFooter />}
      </ConsentCard>
    </AuthLayout>
  )
}
